import { useEffect, useRef, type CSSProperties, type Ref } from "react";
import { ms } from "./speed";
import { diceSfx } from "./sfx";
import { diceVars } from "./diceSkins";

/**
 * تاسِ سه‌بعدی — مکعبِ واقعیِ CSS با شش رو، که پرتاب می‌شود، سه بار به تخته
 * می‌خورد، می‌غلتد و روی عددِ سرور می‌ایستد. پیش‌تر یک مربعِ تخت بود که فقط
 * می‌چرخید.
 *
 * عدد از سرور است و انیمیشن فقط راهِ رسیدن به آن را می‌کشد: چرخش از یک جهتِ
 * تصادفی با چند دورِ کامل شروع می‌شود و درست روی رویی که عدد را رو به بیننده
 * نشان می‌دهد تمام می‌شود. صدای تاس (همان یک فایلِ هر سه بازی، diceSfx) طوری
 * زمان‌بندی می‌شود که اولین تقش با اولین فرودِ تاس یکی باشد.
 *
 * رو‌ها مثلِ تاسِ واقعی‌اند: جمعِ هر دو روی روبه‌رو هفت است (۱↔۶، ۲↔۵، ۳↔۴) و
 * خالِ یک قرمز است.
 */

/** جای خال‌ها در شبکه‌ی ۳×۳ (۰ بالا-راست … ۸ پایین-چپ، راست‌به‌چپ مهم نیست: تاس قرینه است). */
const PIPS: Record<number, number[]> = {
  1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8],
};

/** چرخشِ مکعب (X، Y) که روی v را رو به بیننده می‌آورد — رو‌ها: ۱ جلو، ۶ پشت، ۲ بالا، ۵ پایین، ۳ راست، ۴ چپ. */
const FACE: Record<number, [number, number]> = {
  1: [0, 0], 6: [0, 180], 2: [-90, 0], 5: [90, 0], 3: [0, -90], 4: [0, 90],
};

/** کمی کج تا دو روی کناری هم دیده شوند — تاسِ صاف و رو‌به‌رو تخت به نظر می‌رسد. */
const TILT_X = -14, TILT_Y = 12;

export function Die3D({ value, rolling, size, faded = false, from = [1, -1], seed = 0, sound = true, label, skin }: {
  value: number;
  rolling: boolean;
  /** اندازه‌ی ضلع — هر مقدارِ CSS (مثلاً "52px" یا "7cqw"). */
  size: string;
  faded?: boolean;
  /** سمتِ پرتاب: [x، y] هر کدام −۱..۱ (x مثبت = از راست، y منفی = از بالا). */
  from?: [number, number];
  /** برای دو تاسِ کنارِ هم: کجیِ آرامِ هر کدام فرق کند. */
  seed?: number;
  sound?: boolean;
  label?: string;
  /** تاسِ کسی که انداخته (Seat.dice، مثلاً "dice_lajvard")؛ خالی = عاج. */
  skin?: string;
}) {
  const hop = useRef<HTMLSpanElement>(null);
  const cube = useRef<HTMLSpanElement>(null);
  const shadow = useRef<HTMLSpanElement>(null);
  const v = value >= 1 && value <= 6 ? value : 6;
  const tiltZ = ((seed * 37) % 23) - 11;
  const [fx, fy] = FACE[v]!;
  const rest = `rotateZ(${tiltZ}deg) rotateX(${fx + TILT_X}deg) rotateY(${fy + TILT_Y}deg)`;

  useEffect(() => {
    if (!rolling) return;
    const h = hop.current, c = cube.current, sh = shadow.current;
    if (!h || !c || !sh || typeof c.animate !== "function") return;
    if (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // «حرکتِ کمتر»: بی پرتاب و غلت — تاس روی عددش می‌نشیند، با یک تق
      if (sound) diceSfx();
      return;
    }
    const dur = Math.max(1, ms(820));
    const tx = 2 + Math.floor(Math.random() * 2), ty = 1 + Math.floor(Math.random() * 2);
    const sx = Math.random() < 0.5 ? -1 : 1, sy = Math.random() < 0.5 ? -1 : 1;
    const z0 = tiltZ + (Math.random() * 200 - 100);
    // k: ۰ آغازِ پرتاب … ۱ ایستادن؛ چرخش نرم کم می‌شود و روی همان رو تمام می‌شود
    const spin = (k: number) =>
      `rotateZ(${z0 + (tiltZ - z0) * k}deg) rotateX(${fx + TILT_X + sx * 360 * tx * (1 - k)}deg) rotateY(${fy + TILT_Y + sy * 360 * ty * (1 - k)}deg)`;
    const ox = from[0] * 190, oy = from[1] * 150;   // درصدِ اندازه‌ی خودِ تاس
    // سه برخورد: ۳۰٪، ۶۰٪ و ۷۶٪ ِ زمان؛ بعد فقط چرخشِ آخر آرام می‌شود
    const a1 = c.animate([
      { transform: spin(0) },
      { transform: spin(0.5), offset: 0.3 },
      { transform: spin(0.82), offset: 0.6 },
      { transform: spin(0.95), offset: 0.76 },
      { transform: rest },
    ], { duration: dur, easing: "linear" });
    const a2 = h.animate([
      { transform: `translate(${ox}%, ${oy}%) scale(1.25)`, offset: 0, easing: "cubic-bezier(.4,0,.9,.6)" },
      { transform: `translate(${ox * 0.42}%, 0%) scale(1)`, offset: 0.3, easing: "cubic-bezier(.1,.6,.4,1)" },
      { transform: `translate(${ox * 0.26}%, -32%) scale(1.06)`, offset: 0.45, easing: "cubic-bezier(.5,0,.9,.5)" },
      { transform: `translate(${ox * 0.1}%, 0%) scale(1)`, offset: 0.6, easing: "cubic-bezier(.1,.6,.4,1)" },
      { transform: `translate(${ox * 0.04}%, -10%) scale(1.02)`, offset: 0.68, easing: "cubic-bezier(.5,0,.9,.5)" },
      { transform: "translate(0%, 0%) scale(1)", offset: 0.76 },
      { transform: "translate(0%, 0%) scale(1)", offset: 1 },
    ], { duration: dur, easing: "linear" });
    // سایه روی زمین می‌مانَد: فقط افقی می‌آید و با بالا رفتنِ تاس کم‌رنگ و کوچک می‌شود
    const a3 = sh.animate([
      { transform: `translateX(${ox}%) scale(.55)`, opacity: 0.15, offset: 0 },
      { transform: `translateX(${ox * 0.42}%) scale(1)`, opacity: 0.9, offset: 0.3 },
      { transform: `translateX(${ox * 0.26}%) scale(.8)`, opacity: 0.55, offset: 0.45 },
      { transform: `translateX(${ox * 0.1}%) scale(1)`, opacity: 0.9, offset: 0.6 },
      { transform: "translateX(0%) scale(1)", opacity: 0.9, offset: 1 },
    ], { duration: dur, easing: "linear" });
    // اولین تقِ صدا با فرودِ اول (۳۰٪) یکی می‌شود
    if (sound) diceSfx({ hitAt: dur * 0.3 });
    return () => { a1.cancel(); a2.cancel(); a3.cancel(); };
    // انیمیشن فقط با آغازِ ریختن؛ عددِ تازه همراهِ همان رندر می‌رسد
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolling]);

  return (
    <span className={`d3${faded ? " faded" : ""}`} style={{ "--s": size, ...diceVars(skin) } as CSSProperties}
          role="img" aria-label={label ?? `تاس: ${v}`}>
      <span className="d3-shadow" ref={shadow} />
      <span className="d3-hop" ref={hop}>
        <DieCube cubeRef={cube} transform={rest} />
      </span>
    </span>
  );
}

/** مکعبِ شش‌رو (بی سایه و جهش) — هم Die3D، هم تاسِ پرتابیِ منچ روی تخته (ThrownDie ِ BoardTable.tsx). */
export function DieCube({ cubeRef, transform }: { cubeRef?: Ref<HTMLSpanElement>; transform?: string }) {
  return (
    <span className="d3-cube" ref={cubeRef} style={transform ? { transform } : undefined}>
      {[1, 2, 3, 4, 5, 6].map((f) => <span key={`c${f}`} className={`d3-core k${f}`} />)}
      {[1, 2, 3, 4, 5, 6].map((f) => (
        <span key={f} className={`d3-f k${f}`}>
          {Array.from({ length: 9 }, (_, i) => <i key={i} className={PIPS[f]!.includes(i) ? (f === 1 ? "p red" : "p") : ""} />)}
        </span>
      ))}
    </span>
  );
}
